import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

interface FullscreenDocument extends Document {
  webkitFullscreenElement?: Element | null;
  mozFullScreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
  mozCancelFullScreen?: () => Promise<void> | void;
}

interface FullscreenElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void> | void;
  mozRequestFullScreen?: () => Promise<void> | void;
}

export interface UseFullscreenTwinOptions {
  /** Enables automatic immersive mode when a mobile device rotates to landscape. */
  initialAutoRotateEnabled?: boolean;
}

export interface UseFullscreenTwinResult {
  /** Attach this ref to the element that contains the twin canvas and its overlays. */
  containerRef: RefObject<HTMLDivElement | null>;
  /** True for either native browser fullscreen or the iOS viewport fallback. */
  isFullscreen: boolean;
  /** True when the fixed-viewport fallback is active instead of browser fullscreen. */
  isFallbackFullscreen: boolean;
  /** True only on a mobile viewport in landscape orientation. */
  isLandscape: boolean;
  autoRotateEnabled: boolean;
  setAutoRotateEnabled: (enabled: boolean) => void;
  enterFullscreen: () => Promise<void>;
  exitFullscreen: () => Promise<void>;
  toggleFullscreen: () => Promise<void>;
}

const mobileViewportQuery = [
  '(max-width: 768px)',
  '(max-width: 935px) and (orientation: landscape)',
  '(max-height: 500px)',
].join(', ');

function isMobileViewport(): boolean {
  return window.matchMedia(mobileViewportQuery).matches;
}

function isIOS(): boolean {
  const { userAgent, platform, maxTouchPoints } = navigator;
  return /iPad|iPhone|iPod/i.test(userAgent) || (platform === 'MacIntel' && maxTouchPoints > 1);
}

function getFullscreenElement(): Element | null {
  const fullscreenDocument = document as FullscreenDocument;
  return document.fullscreenElement ?? fullscreenDocument.webkitFullscreenElement ?? fullscreenDocument.mozFullScreenElement ?? null;
}

function lockPageScroll(): () => void {
  const { body, documentElement } = document;
  const bodyOverflow = body.style.overflow;
  const documentOverflow = documentElement.style.overflow;

  body.style.overflow = 'hidden';
  documentElement.style.overflow = 'hidden';

  return () => {
    body.style.overflow = bodyOverflow;
    documentElement.style.overflow = documentOverflow;
  };
}

/**
 * Provides a cross-browser immersive-mode controller for a digital twin container.
 *
 * On iPhone/iPad Safari the hook uses a fixed-viewport overlay rather than relying
 * on element fullscreen, which is not consistently available for arbitrary elements.
 * Consumers apply `fixed inset-0 z-50 h-screen w-screen` when
 * `isFallbackFullscreen` is true.
 */
export function useFullscreenTwin(
  { initialAutoRotateEnabled = true }: UseFullscreenTwinOptions = {},
): UseFullscreenTwinResult {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const restoreScrollRef = useRef<(() => void) | null>(null);
  const autoEnteredRef = useRef(false);
  const [nativeFullscreen, setNativeFullscreen] = useState(false);
  const [isFallbackFullscreen, setIsFallbackFullscreen] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const [autoRotateEnabled, setAutoRotateEnabled] = useState(initialAutoRotateEnabled);

  const activateFallback = useCallback(() => {
    if (!restoreScrollRef.current) {
      restoreScrollRef.current = lockPageScroll();
    }
    setIsFallbackFullscreen(true);
  }, []);

  const deactivateFallback = useCallback(() => {
    restoreScrollRef.current?.();
    restoreScrollRef.current = null;
    setIsFallbackFullscreen(false);
  }, []);

  const enterFullscreen = useCallback(async () => {
    const container = containerRef.current;
    if (!container) return;

    // iPhone Safari does not offer reliable arbitrary-element fullscreen.
    if (isIOS()) {
      activateFallback();
      return;
    }

    const fullscreenElement = container as FullscreenElement;
    const requestFullscreen = fullscreenElement.requestFullscreen
      ?? fullscreenElement.webkitRequestFullscreen
      ?? fullscreenElement.mozRequestFullScreen;

    if (!requestFullscreen) {
      activateFallback();
      return;
    }

    try {
      await requestFullscreen.call(fullscreenElement);
    } catch {
      // Browser policies can reject non-gesture fullscreen requests (including
      // automatic orientation changes), so preserve the immersive experience.
      activateFallback();
    }
  }, [activateFallback]);

  const exitFullscreen = useCallback(async () => {
    autoEnteredRef.current = false;
    deactivateFallback();

    const fullscreenDocument = document as FullscreenDocument;
    const exit = document.exitFullscreen
      ?? fullscreenDocument.webkitExitFullscreen
      ?? fullscreenDocument.mozCancelFullScreen;

    if (getFullscreenElement() && exit) {
      try {
        await exit.call(fullscreenDocument);
      } catch {
        // The browser may already have exited fullscreen (for example via Escape).
      }
    }
  }, [deactivateFallback]);

  const toggleFullscreen = useCallback(async () => {
    if (nativeFullscreen || isFallbackFullscreen) {
      await exitFullscreen();
      return;
    }
    await enterFullscreen();
  }, [enterFullscreen, exitFullscreen, isFallbackFullscreen, nativeFullscreen]);

  useEffect(() => {
    const syncNativeFullscreen = () => {
      const nextNativeFullscreen = getFullscreenElement() === containerRef.current;
      setNativeFullscreen(nextNativeFullscreen);
      if (nextNativeFullscreen) deactivateFallback();
    };

    document.addEventListener('fullscreenchange', syncNativeFullscreen);
    document.addEventListener('webkitfullscreenchange', syncNativeFullscreen);
    document.addEventListener('mozfullscreenchange', syncNativeFullscreen);
    syncNativeFullscreen();

    return () => {
      document.removeEventListener('fullscreenchange', syncNativeFullscreen);
      document.removeEventListener('webkitfullscreenchange', syncNativeFullscreen);
      document.removeEventListener('mozfullscreenchange', syncNativeFullscreen);
    };
  }, [deactivateFallback]);

  useEffect(() => {
    const orientationQuery = window.matchMedia('(orientation: landscape)');
    const mobileQuery = window.matchMedia(mobileViewportQuery);

    const syncOrientation = () => {
      setIsLandscape(mobileQuery.matches && orientationQuery.matches);
    };

    window.addEventListener('orientationchange', syncOrientation);
    window.addEventListener('resize', syncOrientation);
    orientationQuery.addEventListener('change', syncOrientation);
    mobileQuery.addEventListener('change', syncOrientation);
    syncOrientation();

    return () => {
      window.removeEventListener('orientationchange', syncOrientation);
      window.removeEventListener('resize', syncOrientation);
      orientationQuery.removeEventListener('change', syncOrientation);
      mobileQuery.removeEventListener('change', syncOrientation);
    };
  }, []);

  useEffect(() => {
    if (!autoRotateEnabled || !isMobileViewport()) return;

    if (isLandscape && !nativeFullscreen && !isFallbackFullscreen) {
      autoEnteredRef.current = true;
      void enterFullscreen();
      return;
    }

    if (!isLandscape && autoEnteredRef.current && (nativeFullscreen || isFallbackFullscreen)) {
      void exitFullscreen();
    }
  }, [autoRotateEnabled, enterFullscreen, exitFullscreen, isFallbackFullscreen, isLandscape, nativeFullscreen]);

  useEffect(() => {
    return () => {
      restoreScrollRef.current?.();
      restoreScrollRef.current = null;
    };
  }, []);

  return {
    containerRef,
    isFullscreen: nativeFullscreen || isFallbackFullscreen,
    isFallbackFullscreen,
    isLandscape,
    autoRotateEnabled,
    setAutoRotateEnabled,
    enterFullscreen,
    exitFullscreen,
    toggleFullscreen,
  };
}
